import React, { useState, useEffect, useCallback, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Bell,
  Clock,
  Plus,
  Users,
  Building2,
  ArrowRight,
  Timer,
  MoreVertical,
  X,
  UserRound,
  UserCheck,
  Tag,
  Calendar,
  Download,
  AlertTriangle,
  ChevronDown,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { incidentColors } from "../../theme/theme";

import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import SuccessPopup from "../../components/SuccessPopup";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { DataTable } from "../../components/DataTable";

// Componentes específicos de tickets
import { CreateIncidentModal } from "./CreateIncidentModal";
import { TimeLogModal } from "./TimeLogModal";
import {
  DateRangeFilterField,
  IncidentFiltersModal,
} from "../../components/IncidentFiltersModal";
import { AgencyIcon } from "../../components/icons/AgencyIcon";
import { UnifiedModal } from "../../components/UnifiedModal";
import { CustomSelector } from "../../components/CustomSelector";
import {
  downloadCSV,
  getIncidentCSVHeaders,
  getPurchaseHistoryCSVHeaders,
  getPurchaseSummaryCSVHeaders,
  getStaffSummaryCSVHeaders,
  getStaffTicketCSVHeaders,
  incidentToCSVRow,
  purchaseHistoryToCSVRow,
  purchaseSummaryToCSVRow,
  staffSummaryToCSVRow,
  staffTicketToCSVRow,
} from "../../utils/csvUtils";
import { Label } from "../../components/Label";
import { UnreadThreadBadge } from "../../components/UnreadThreadBadge";
import { Avatar } from "../../components/Avatar";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { isClientReadOnly } from "../../utils/clientAccess";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { buildDateRangeChipLabel } from "../../utils/dateUtils";
import { isNoHoursError } from "../../services/ticketHoursService";
import { BUY_HOURS_PATH } from "../Settings/settingsPaths";

// === FUNCIONES UTILITARIAS ===

// Mapeo de estados de incidentes a colores del Badge
const incidentStatusColorMap = {
  new: "red",
  open: "yellow",
  closed: "green",
};

const canCreateIncidents = (user) => {
  return ["agency_admin", "consultant"].includes(user.role);
};

const canManageIncident = (user, incident) => {
  if (!user || !incident) return { canRecognize: false, canComplete: false };

  if (!["superadmin", "freelance_ticket"].includes(user.role)) {
    return { canRecognize: false, canComplete: false };
  }

  return {
    canRecognize: incident.status === "new",
    canComplete: incident.status === "open",
  };
};

// === APIS DEL BACKEND ===

/**
 * Hook para obtener todos los clientes
 */
const useClients = (interceptorReady, shouldFetch = true, refreshDeps = []) => {
  return getData("/clients/options/?ordering=name", interceptorReady && shouldFetch, refreshDeps);
};

/**
 * Hook para obtener todos los tickets
 */
const useIncidents = (
  interceptorReady,
  shouldFetch,
  filters = {},
  refreshDeps = []
) => {
  const queryParams = new URLSearchParams();

  if (filters.status && filters.status !== "all") {
    // Si el status contiene comas, agregar cada status por separado
    if (filters.status.includes(",")) {
      filters.status.split(",").forEach((status) => {
        queryParams.append("status", status.trim());
      });
    } else {
      queryParams.append("status", filters.status);
    }
  }
  if (filters.agency) {
    queryParams.append("agency", filters.agency);
  }
  if (filters.client) {
    queryParams.append("client", filters.client);
  }
  if (filters.created_by) {
    queryParams.append("created_by", filters.created_by);
  }
  if (filters.assigned_to) {
    queryParams.append("assigned_to", filters.assigned_to);
  }
  if (filters.category) {
    // Si es un array, agregar cada categoría por separado
    if (Array.isArray(filters.category)) {
      filters.category.forEach((cat) => {
        queryParams.append("category", cat);
      });
    } else {
      queryParams.append("category", filters.category);
    }
  }
  if (filters.created_at_after) {
    queryParams.append("created_at_after", filters.created_at_after);
  }
  if (filters.created_at_before) {
    queryParams.append("created_at_before", filters.created_at_before);
  }
  if (filters.search) {
    queryParams.append("search", filters.search);
  }
  if (filters.page) {
    queryParams.append("page", filters.page);
  }
  if (filters.page_size) {
    queryParams.append("page_size", filters.page_size);
  }

  const endpoint = `/communication/tickets/${queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

  return getData(endpoint, interceptorReady && shouldFetch, refreshDeps);
};


/**
 * Crea un nuevo ticket
 */
const createIncident = async (incidentData) => {
  try {
    const formData = new FormData();
    formData.append("title", incidentData.title);
    formData.append("description", incidentData.description);
    formData.append("is_urgent", incidentData.is_urgent ? "true" : "false");

    if (incidentData.client_id) {
      formData.append("client_id", incidentData.client_id);
    }

    if (incidentData.ticket_image_file) {
      formData.append("ticket_image", incidentData.ticket_image_file);
    }

    const response = await apiClient.post("/communication/tickets/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creando ticket:", error);
    throw error;
  }
};

/**
 * Abre un ticket usando PATCH directo
 */
const openTicket = async (ticketId) => {
  try {
    const response = await apiClient.patch(
      `/communication/tickets/${ticketId}/`,
      {
        status: "open",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error abriendo ticket:", error);
    throw error;
  }
};

/**
 * Cierra un ticket usando PATCH directo
 */
const closeTicket = async (ticketId) => {
  try {
    const response = await apiClient.patch(
      `/communication/tickets/${ticketId}/`,
      {
        status: "closed",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error cerrando ticket:", error);
    throw error;
  }
};

// Ya no necesitamos transformar los datos del backend - los usamos directamente

// === CONFIGURACIONES ===

const statusConfig = {
  new: {
    label: "Nuevo",
    color: incidentColors.status.new.className,
  },
  open: {
    label: "Abierto",
    color: incidentColors.status.open.className,
  },
  closed: {
    label: "Cerrado",
    color: incidentColors.status.closed.className,
  },
};

// Componente encapsulado para la bolita de indicador
const FilterIndicatorDot = ({ theme }) => (
  <Box
    sx={{
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      flexShrink: 0,
    }}
  />
);

const createEmptyAdvancedFilters = () => ({
  agency: null,
  client: null,
  createdBy: null,
  assignedTo: null,
  category: null,
  createdAtAfter: "",
  createdAtBefore: "",
});

const cloneAdvancedFilters = (filters) => ({
  agency: filters.agency ? { ...filters.agency } : null,
  client: filters.client ? { ...filters.client } : null,
  createdBy: filters.createdBy ? { ...filters.createdBy } : null,
  assignedTo: filters.assignedTo ? { ...filters.assignedTo } : null,
  category: filters.category ? { ...filters.category } : null,
  createdAtAfter: filters.createdAtAfter || "",
  createdAtBefore: filters.createdAtBefore || "",
});

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

export function Incidents() {
  const { user: currentUser, interceptorReady } = useAuth();
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    incidentsRefreshKey,
    filterRefresh,
  } = useOutletContext();
  const theme = useTheme();
  const { colors, neutrals } = useCustomTheme();
  const navigate = useNavigate();

  // Estados locales
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  // Sistema de filtros: selección única
  const [selectedStatus, setSelectedStatus] = useState("all"); // Por defecto: todos
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [clientsRefresh, setClientsRefresh] = useState(0);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupError, setIsPopupError] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );

  // Estados para gestión de tiempos
  const [timeLogModalOpen, setTimeLogModalOpen] = useState(false);
  const [selectedTicketForTimeLog, setSelectedTicketForTimeLog] =
    useState(null);
  const [ticketDetailsForTimeLog, setTicketDetailsForTimeLog] = useState(null);
  const [isLoadingTicketDetails, setIsLoadingTicketDetails] = useState(false);

  // Estados para menú desplegable de acciones
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedIncidentForMenu, setSelectedIncidentForMenu] = useState(null);

  // Estado para controlar el hover de botones
  const [isHoveringButtons, setIsHoveringButtons] = useState(false);

  // Estados para modal de asignar y clasificar
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedTicketForAssign, setSelectedTicketForAssign] = useState(null);
  const [assignFormData, setAssignFormData] = useState({
    assigned_to: null,
    category: null,
  });

  // Estados para paginación de tabla
  const [currentPage, setCurrentPage] = useState(0); // TablePagination usa índice base 0
  const [pageSize, setPageSize] = useState(25);
  const [allIncidents, setAllIncidents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusStats, setStatusStats] = useState({
    new: 0,
    open: 0,
    closed: 0,
  });
  const [totalMinutesConsumed, setTotalMinutesConsumed] = useState(0);

  // Estados para modal de descarga CSV
  const [isCSVModalOpen, setIsCSVModalOpen] = useState(false);
  // "agencies": tickets + bonos de horas por agencia; "staff": tickets y horas por freelancer
  const [csvExportType, setCsvExportType] = useState("agencies");
  const [csvDateRange, setCSVDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [csvAgency, setCsvAgency] = useState(null);

  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === '' && lastSearchRef.current !== '') {
        lastSearchRef.current = '';
        setSearchTerm('');
        setCurrentPage(0);
        setAllIncidents([]);
        setTotalCount(0);
        setSearchRefreshKey(prev => prev + 1);
        return;
      }

      // CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const isAgencyScopedUser = ["agency_admin", "consultant"].includes(
    currentUser?.role
  );
  const shouldFetchModalAgencies =
    (isFilterModalOpen || isCSVModalOpen) && currentUser?.role === "superadmin";
  const derivedAgencyForClients = React.useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === "superadmin") {
      const fallbackAgency =
        appliedAdvancedFilters.agency?.value || selectedFilter || null;
      if (isFilterModalOpen) {
        return draftAdvancedFilters.agency?.value || fallbackAgency;
      }
      return fallbackAgency;
    }
    return currentUser.agencyId || null;
  }, [
    currentUser,
    draftAdvancedFilters.agency,
    appliedAdvancedFilters.agency,
    selectedFilter,
    isFilterModalOpen,
  ]);
  const shouldFetchModalClients =
    isFilterModalOpen &&
    (currentUser?.role === "superadmin" || isAgencyScopedUser);
  const filterClientsEndpoint = React.useMemo(() => {
    const params = new URLSearchParams();
    params.append("ordering", "name");
    if (currentUser?.role === "superadmin") {
      if (derivedAgencyForClients) {
        params.append("agency", derivedAgencyForClients);
      }
    }
    // Note: For non-superadmin, backend already filters by user's agency/access
    const queryString = params.toString();
    return `/clients/options/${queryString ? `?${queryString}` : ""}`;
  }, [currentUser?.role, derivedAgencyForClients]);
  const { data: filterAgenciesData, isLoading: isLoadingFilterAgencies } =
    getData(
      "/agencies/",
      interceptorReady && shouldFetchModalAgencies,
      [incidentsRefreshKey, filterRefresh]
    );
  const { data: filterClientsData, isLoading: isLoadingFilterClients } = getData(
    filterClientsEndpoint,
    interceptorReady && shouldFetchModalClients,
    [
      filterClientsEndpoint,
      draftAdvancedFilters.agency?.value,
      currentUser?.agencyId,
    ]
  );
  const agencyOptions = React.useMemo(() => {
    if (!filterAgenciesData) return [];
    return normalizeApiList(filterAgenciesData).map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
  }, [filterAgenciesData]);
  const csvAgencyOptions = React.useMemo(
    () => [{ value: "__any__", label: "Mostrar todas" }, ...agencyOptions],
    [agencyOptions]
  );
  const clientOptions = React.useMemo(() => {
    if (!filterClientsData) return [];
    const list = normalizeApiList(filterClientsData);

    // Determinar si hay agencia seleccionada (en el modal usar draft, fuera usar applied)
    const hasAgencySelected = isFilterModalOpen
      ? draftAdvancedFilters.agency?.value
      : appliedAdvancedFilters.agency?.value;

    return list.map((client) => {
      const agencyName =
        client.agency?.name ||
        client.agency_info?.name ||
        client.agency_name ||
        null;

      if (currentUser?.role === "superadmin") {
        // Para superadmin: mostrar "cliente - agencia" solo cuando NO hay agencia seleccionada
        const showAgencyInLabel = !hasAgencySelected;
        return {
          value: client.id,
          label: showAgencyInLabel && agencyName ? `${client.name} - ${agencyName}` : client.name,
          agency:
            client.agency ||
            client.agency_id ||
            client.agency?.id ||
            client.agency_info?.id ||
            null,
        };
      } else {
        // Para admin_agency y consultant: siempre mostrar solo el nombre del cliente
        return {
          value: client.id,
          label: client.name,
          agency:
            client.agency ||
            client.agency_id ||
            client.agency?.id ||
            client.agency_info?.id ||
            null,
        };
      }
    });
  }, [filterClientsData, appliedAdvancedFilters.agency, draftAdvancedFilters.agency, isFilterModalOpen, currentUser?.role]);
  const globalAgencyOption = React.useMemo(() => {
    if (
      currentUser?.role !== "superadmin" ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [currentUser?.role, selectedFilter, selectedFilterData]);
  const globalClientOption = React.useMemo(() => {
    if (
      !isAgencyScopedUser ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [isAgencyScopedUser, selectedFilter, selectedFilterData]);
  const isGlobalAgencyLocked =
    currentUser?.role === "superadmin" && Boolean(globalAgencyOption);
  const isGlobalClientLocked =
    isAgencyScopedUser && Boolean(globalClientOption);
  const modalAgencyForWorkers = React.useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === "superadmin") {
      return (
        draftAdvancedFilters.agency?.value ||
        globalAgencyOption?.value ||
        appliedAdvancedFilters.agency?.value ||
        null
      );
    }
    return currentUser.agencyId || null;
  }, [
    currentUser,
    draftAdvancedFilters.agency,
    globalAgencyOption,
    appliedAdvancedFilters.agency,
  ]);

  // Para superadmin: si hay agencia seleccionada, traer sus workers; sino, traer todos los users
  const shouldFetchCreatorOptions = isFilterModalOpen;
  const creatorEndpoint = React.useMemo(() => {
    if (currentUser?.role === "superadmin") {
      if (modalAgencyForWorkers) {
        return `/agencies/${modalAgencyForWorkers}/?expand=workers`;
      }
      // Si no hay agencia seleccionada, traer todos los usuarios
      return "/agencies/?expand=workers";
    }
    // Para otros roles, siempre usar la agencia del usuario
    if (modalAgencyForWorkers) {
      return `/agencies/${modalAgencyForWorkers}/?expand=workers`;
    }
    return "";
  }, [currentUser?.role, modalAgencyForWorkers]);

  const {
    data: agencyWorkersResponse,
    isLoading: isLoadingAgencyWorkers,
  } = getData(
    creatorEndpoint,
    interceptorReady && shouldFetchCreatorOptions && creatorEndpoint !== "",
    [creatorEndpoint]
  );

  const creatorOptions = React.useMemo(() => {
    // Si es la respuesta de /agencies/?expand=workers (superadmin sin agencia)
    if (Array.isArray(agencyWorkersResponse)) {
      // Combinar todos los workers de todas las agencias
      const allWorkers = agencyWorkersResponse.reduce((acc, agency) => {
        if (Array.isArray(agency.workers)) {
          return [...acc, ...agency.workers];
        }
        return acc;
      }, []);

      // Filtrar por roles y eliminar duplicados por id
      const uniqueWorkers = allWorkers.reduce((acc, worker) => {
        if (
          ["agency_admin", "consultant"].includes(worker.role) &&
          !acc.some((w) => w.id === worker.id)
        ) {
          acc.push(worker);
        }
        return acc;
      }, []);

      return uniqueWorkers.map((worker) => ({
        value: worker.id,
        label:
          `${worker.first_name || ""} ${worker.last_name || ""}`.trim() ||
          worker.email ||
          `Usuario ${worker.id}`,
      }));
    }

    // Si es la respuesta de /agencies/{id}/?expand=workers
    const workers = agencyWorkersResponse?.workers;
    if (!Array.isArray(workers)) return [];
    return workers
      .filter((worker) =>
        ["agency_admin", "consultant"].includes(worker.role)
      )
      .map((worker) => ({
        value: worker.id,
        label:
          `${worker.first_name || ""} ${worker.last_name || ""}`.trim() ||
          worker.email ||
          `Usuario ${worker.id}`,
      }));
  }, [agencyWorkersResponse]);

  const creatorPlaceholderText = "Selecciona un usuario";

  // Opciones para "Asignado a" (solo superadmins)
  const {
    data: superadminsResponse,
    isLoading: isLoadingSuperadmins,
  } = getData(
    "/users/?role=superadmin",
    interceptorReady && currentUser?.role === "superadmin",
    []
  );
  const { data: ticketFreelancersResponse } = getData(
    "/users/?role=freelance_ticket",
    interceptorReady && currentUser?.role === "superadmin",
    []
  );

  const assignedToOptions = React.useMemo(() => {
    const assignableUsers = [
      ...normalizeApiList(superadminsResponse),
      ...normalizeApiList(ticketFreelancersResponse),
    ];
    return assignableUsers.map((user) => ({
      value: user.id,
      label:
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        user.email ||
        `Usuario ${user.id}`,
    }));
  }, [superadminsResponse, ticketFreelancersResponse]);

  // Opciones para "Categoría"
  const categoryOptions = [
    { value: "soporte", label: "Soporte" },
    { value: "mentoria", label: "Mentoría" },
    { value: "consulta", label: "Consulta" },
    { value: "kit_digital", label: "Kit Digital" },
    { value: "comercial", label: "Comercial" },
  ];

  const resetPaginationState = useCallback(() => {
    setCurrentPage(0);
    setAllIncidents([]);
    setTotalCount(0);
  }, []);

  // Construir el valor del filtro basado en el estado seleccionado
  const statusFilterValue = React.useMemo(() => {
    return selectedStatus; // "all", "new", "open", o "closed"
  }, [selectedStatus]);

  const filterSignature = React.useMemo(
    () =>
      JSON.stringify({
        search: searchTerm,
        status: statusFilterValue,
        selectedStatus: selectedStatus,
        selectedFilter: selectedFilter || null,
        role: currentUser?.role || null,
        filterRefresh,
        incidentsRefreshKey,
        advancedFilters: {
          agency: appliedAdvancedFilters.agency?.value || null,
          client: appliedAdvancedFilters.client?.value || null,
          createdBy: appliedAdvancedFilters.createdBy?.value || null,
          assignedTo: appliedAdvancedFilters.assignedTo?.value || null,
          category: appliedAdvancedFilters.category?.value || null,
          createdAtAfter: appliedAdvancedFilters.createdAtAfter || null,
          createdAtBefore: appliedAdvancedFilters.createdAtBefore || null,
        },
      }),
    [
      searchTerm,
      statusFilterValue,
      selectedStatus,
      selectedFilter,
      currentUser?.role,
      filterRefresh,
      incidentsRefreshKey,
      appliedAdvancedFilters,
    ]
  );

  const filterSignatureRef = React.useRef(filterSignature);
  const filtersChanged = filterSignature !== filterSignatureRef.current;

  useEffect(() => {
    if (filtersChanged) {
      filterSignatureRef.current = filterSignature;
      if (currentPage !== 0) {
        resetPaginationState();
      }
    }
  }, [filtersChanged, filterSignature, currentPage, resetPaginationState]);

  // Efecto para limpiar filtros personalizados cuando cambia el selectedFilter del AppLayout
  const previousSelectedFilterRef = React.useRef(selectedFilter);
  useEffect(() => {
    if (previousSelectedFilterRef.current !== selectedFilter) {
      previousSelectedFilterRef.current = selectedFilter;

      // Si hay filtros personalizados activos, limpiarlos
      const hasAdvancedFilters =
        appliedAdvancedFilters.agency?.value ||
        appliedAdvancedFilters.client?.value ||
        appliedAdvancedFilters.createdBy?.value ||
        appliedAdvancedFilters.assignedTo?.value ||
        appliedAdvancedFilters.category?.value ||
        appliedAdvancedFilters.createdAtAfter ||
        appliedAdvancedFilters.createdAtBefore;

      if (hasAdvancedFilters) {
        setAppliedAdvancedFilters(createEmptyAdvancedFilters());
        setDraftAdvancedFilters(createEmptyAdvancedFilters());
        resetPaginationState();
        setRefresh((prev) => prev + 1);
      }
    }
  }, [selectedFilter, appliedAdvancedFilters, resetPaginationState]);

  const filters = {
    search: searchTerm,
    status: statusFilterValue,
    page: currentPage + 1, // Backend espera páginas base 1
    page_size: pageSize,
  };
  const advancedAgencyId = appliedAdvancedFilters.agency?.value || null;
  if (currentUser?.role === "superadmin") {
    if (advancedAgencyId) {
      filters.agency = advancedAgencyId;
    } else if (selectedFilter) {
      filters.agency = selectedFilter;
    }
    if (appliedAdvancedFilters.client?.value) {
      filters.client = appliedAdvancedFilters.client.value;
    }
  } else if (isAgencyScopedUser) {
    if (appliedAdvancedFilters.client?.value) {
      filters.client = appliedAdvancedFilters.client.value;
    } else if (selectedFilter) {
      filters.client = selectedFilter;
    }
  }
  if (appliedAdvancedFilters.createdBy?.value) {
    filters.created_by = appliedAdvancedFilters.createdBy.value;
  }
  if (appliedAdvancedFilters.assignedTo?.value) {
    filters.assigned_to = appliedAdvancedFilters.assignedTo.value;
  }
  if (appliedAdvancedFilters.category?.value) {
    filters.category = appliedAdvancedFilters.category.value;
  }
  if (appliedAdvancedFilters.createdAtAfter) {
    filters.created_at_after = appliedAdvancedFilters.createdAtAfter;
  }
  if (appliedAdvancedFilters.createdAtBefore) {
    filters.created_at_before = appliedAdvancedFilters.createdAtBefore;
  }

  // Hook para obtener tickets del backend
  const { data: backendIncidents, isLoading: isLoadingIncidents } =
    useIncidents(interceptorReady, !!currentUser, filters, [
      refresh,
      incidentsRefreshKey,
      filterRefresh,
      currentPage,
      pageSize,
      searchRefreshKey,
    ]);

  // Hook para obtener clientes (solo cuando se abre el modal de nuevo ticket)
  const { data: backendClients } = useClients(
    interceptorReady,
    canCreateIncidents(currentUser) && isCreateModalOpen,
    [clientsRefresh, incidentsRefreshKey]
  );


  // Efecto para resetear paginación cuando cambian los filtros
  useEffect(() => {
    resetPaginationState();
  }, [refresh, incidentsRefreshKey, filterRefresh, resetPaginationState]);

  // Efecto para manejar los tickets del backend y stats
  useEffect(() => {
    if (backendIncidents) {
      // Actualizar estadísticas globales
      if (backendIncidents.status_stats) {
        setStatusStats(backendIncidents.status_stats);
      }

      // Actualizar total de minutos consumidos
      if (backendIncidents.total_minutes_consumed !== undefined) {
        setTotalMinutesConsumed(backendIncidents.total_minutes_consumed);
      }

      if (backendIncidents.results) {
        // Reemplazar tickets con los de la página actual
        setAllIncidents(backendIncidents.results);
      }

      // Actualizar el total de tickets
      if (backendIncidents.count !== undefined) {
        setTotalCount(backendIncidents.count);
      }
    }
  }, [backendIncidents]);

  // Funciones para manejar cambios de página y tamaño de página
  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0); // Resetear a la primera página
  };
  const handleOpenFilterModal = () => {
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
    setIsFilterModalOpen(true);
  };
  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
  };
  const handleDraftFilterChange = (field, value) => {
    setDraftAdvancedFilters((prev) => {
      // Para campos de fecha, el valor es un string directo
      // Para los demás campos (selectors), el valor es un objeto con .value
      const isDateField = field === "createdAtAfter" || field === "createdAtBefore";
      const nextValue = isDateField
        ? (value || "")
        : (value ? { ...value } : null);

      const updated = { ...prev, [field]: nextValue };
      if (field === "agency") {
        updated.client = null;
        updated.createdBy = null;
      }
      return updated;
    });
  };
  const handleClearDraftFilters = () => {
    setDraftAdvancedFilters(createEmptyAdvancedFilters());
  };
  const handleApplyAdvancedFilters = () => {
    setAppliedAdvancedFilters(cloneAdvancedFilters(draftAdvancedFilters));
    setIsFilterModalOpen(false);
    resetPaginationState();
    setRefresh((prev) => prev + 1);
  };
  const handleRemoveAdvancedFilter = (key) => {
    setAppliedAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);

      // Caso especial: si se elimina el chip de fecha, limpiar ambos campos
      if (key === "createdAt") {
        next.createdAtAfter = "";
        next.createdAtBefore = "";
      } else {
        next[key] = null;
      }

      // No limpiar createdBy cuando se borra la agencia, ya que cuando no hay agencia
      // seleccionada, se pueden elegir usuarios de todas las agencias
      // if (key === "agency") {
      //   next.createdBy = null;
      // }
      return next;
    });
    setDraftAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);

      // Caso especial: si se elimina el chip de fecha, limpiar ambos campos
      if (key === "createdAt") {
        next.createdAtAfter = "";
        next.createdAtBefore = "";
      } else {
        next[key] = null;
      }

      // No limpiar createdBy cuando se borra la agencia, ya que cuando no hay agencia
      // seleccionada, se pueden elegir usuarios de todas las agencias
      // if (key === "agency") {
      //   next.createdBy = null;
      // }
      return next;
    });
    resetPaginationState();
    setRefresh((prev) => prev + 1);
  };

  // Mapear correctamente los datos del backend y almacenar toda la información del ticket
  const incidents = React.useMemo(() => {
    if (!allIncidents) return [];

    // Almacenar toda la información completa del ticket según el formato del backend
    return allIncidents.map((ticket) => {
      return {
        id: ticket.id,
        title: ticket.title,
        description: ticket.description,
        status: ticket.status,
        agency: ticket.agency_info,
        client: ticket.client_info,
        created_by: {
          id: ticket.created_by?.id,
          full_name: ticket.created_by?.full_name,
          email: ticket.created_by?.email,
          role: ticket.created_by?.role,
          profile_image: ticket.created_by?.profile_image,
        },
        thread_id: ticket.thread_id,
        created_at: ticket.created_at,
        updated_at: ticket.updated_at,
        metrics: {
          total_messages: ticket.metrics?.total_messages || 0,
          total_root_messages: ticket.metrics?.total_root_messages || 0,
          total_comments: ticket.metrics?.total_comments || 0,
          open_actions: ticket.metrics?.open_actions || 0,
          resolved_actions: ticket.metrics?.resolved_actions || 0,
          active_important_notices:
            ticket.metrics?.active_important_notices || 0,
          first_response_minutes: ticket.metrics?.first_response_minutes,
          last_message_at: ticket.metrics?.last_message_at,
        },
        unopened_unread_notifications: ticket.unopened_unread_notifications || 0,
        // Incluir información de tiempo si está disponible en el listado
        time_log: ticket.time_log || null,
        agency_hour_balance: ticket.agency_hour_balance || null,
        // Incluir información de asignación y categoría
        assigned_to_info: ticket.assigned_to_info || null,
        category: ticket.category || null,
        is_urgent: !!ticket.is_urgent,
      };
    });
  }, [allIncidents]);

  const visibleIncidents = incidents;
  const advancedFilterChips = React.useMemo(() => {
    const chips = [];

    if (appliedAdvancedFilters.agency) {
      chips.push({
        key: "agency",
        label: appliedAdvancedFilters.agency.label,
      });
    }
    if (appliedAdvancedFilters.client) {
      chips.push({
        key: "client",
        label: appliedAdvancedFilters.client.label,
      });
    }
    if (appliedAdvancedFilters.createdBy) {
      chips.push({
        key: "createdBy",
        label: `Creado por: ${appliedAdvancedFilters.createdBy.label}`,
      });
    }
    if (appliedAdvancedFilters.assignedTo) {
      chips.push({
        key: "assignedTo",
        label: `Asignado a: ${appliedAdvancedFilters.assignedTo.label}`,
      });
    }
    if (appliedAdvancedFilters.category) {
      chips.push({
        key: "category",
        label: `Categoría: ${appliedAdvancedFilters.category.label}`,
      });
    }
    // Chip para rango de fechas
    if (appliedAdvancedFilters.createdAtAfter || appliedAdvancedFilters.createdAtBefore) {
      chips.push({
        key: "createdAt",
        label: buildDateRangeChipLabel({
          label: "Creación",
          start: appliedAdvancedFilters.createdAtAfter,
          end: appliedAdvancedFilters.createdAtBefore,
        }),
      });
    }
    return chips;
  }, [appliedAdvancedFilters]);
  const handleClearGlobalFilter = useCallback(() => {
    changeSelectedFilter("global");
  }, [changeSelectedFilter]);
  const globalFilterChip = React.useMemo(() => {
    if (!selectedFilter || !selectedFilterData?.name) {
      return null;
    }
    return {
      key: "global-filter",
      label: selectedFilterData.name,
      onRemove: handleClearGlobalFilter,
    };
  }, [selectedFilter, selectedFilterData, handleClearGlobalFilter]);
  const displayedFilterChips = React.useMemo(() => {
    const chips = [];
    if (globalFilterChip) {
      chips.push(globalFilterChip);
    }
    advancedFilterChips.forEach((chip) => {
      chips.push({
        ...chip,
        onRemove: () => handleRemoveAdvancedFilter(chip.key),
      });
    });
    return chips;
  }, [globalFilterChip, advancedFilterChips, handleRemoveAdvancedFilter]);

  // Función para cambiar el filtro de estado (selección única)
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    resetPaginationState();
    setRefresh((prev) => prev + 1);
  };

  const getHeaderInfo = () => {
    switch (currentUser.role) {
      case "superadmin":
        return {
          title: "Gestión de Tickets",
          subtitle: "Supervisa tickets de tus clientes",
        };
      case "agency_admin":
        return {
          title: "Tickets",
          subtitle: "Gestiona tickets de tus clientes",
        };
      case "consultant":
        return {
          title: "Mis Tickets",
          subtitle: "Gestiona tickets de tus clientes",
        };
      case "freelance_ticket":
        return {
          title: "Mis Tickets",
          subtitle: "Gestiona únicamente los tickets que tienes asignados",
        };
      default:
        return {
          title: "Tickets",
          subtitle: "Lista de tickets",
        };
    }
  };

  const headerInfo = getHeaderInfo();

  // Función para refrescar tickets
  const refreshIncidents = () => {
    setRefresh((prev) => prev + 1);
  };

  // Set page context
  React.useEffect(() => {
    if (currentUser) {
      // Botones de la derecha según el rol
      let rightContent = null;

      if (currentUser.role === "superadmin") {
        // Superadmin: botón de descargar CSV
        rightContent = (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Download size={16} />
                  Descargar CSV
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleOpenCSVModal("agencies")}>
                  <Clock size={16} />
                  Tickets y horas por agencia
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenCSVModal("staff")}>
                  <UserCheck size={16} />
                  Tickets y horas por freelancer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Box>
        );
      } else if (canCreateIncidents(currentUser)) {
        // Agency admin o consultant: botón de nuevo ticket
        if (currentUser.role === "agency_admin") {
          rightContent = (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="dark">
                    <Timer size={16} />
                    Bonos de horas
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(BUY_HOURS_PATH)}>
                    <Clock size={16} />
                    Comprar horas
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleOpenCSVModal("agencies")}>
                    <Download size={16} />
                    Descargar compras CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={() => {
                  setClientsRefresh((prev) => prev + 1); // Refrescar clientes cada vez que se abre el modal
                  setIsCreateModalOpen(true);
                }}
              >
                <Plus size={16} />
                Nuevo Ticket
              </Button>
            </Box>
          );
        } else {
          rightContent = (
            <Button
              onClick={() => {
                setClientsRefresh((prev) => prev + 1); // Refrescar clientes cada vez que se abre el modal
                setIsCreateModalOpen(true);
              }}
            >
              <Plus size={16} />
              Nuevo Ticket
            </Button>
          );
        }
      }

      // Breadcrumb para superadmin
      let breadcrumbItems = null;
      if (currentUser.role === "superadmin") {
        if (selectedFilter && selectedFilterData) {
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Agencia: ${selectedFilterData.name}` },
          ];
        } else {
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      } else if (
        currentUser.role === "agency_admin" ||
        currentUser.role === "consultant"
      ) {
        if (selectedFilter && selectedFilterData) {
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Cliente: ${selectedFilterData.name}` },
          ];
        } else {
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      }

      setPageContext({
        title: headerInfo.title,
        titleRightContent: (
          <Tooltip title="Total horas consumidas (filtros aplicados)">
            <Badge
              variant="secondary"
              size="lg"
              sx={{
                borderRadius: "12px",
                fontWeight: 500,
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Timer size={14} />
              {totalMinutesConsumed >= 60
                ? `${Math.floor(totalMinutesConsumed / 60)}h ${totalMinutesConsumed % 60}m`
                : `${totalMinutesConsumed}m`
              }
            </Badge>
          </Tooltip>
        ),
        subtitle: headerInfo.subtitle,
        showTitle: true,
        rightContent: rightContent,
        breadcrumbItems,
      });
    }
  }, [
    currentUser,
    setPageContext,
    headerInfo.title,
    headerInfo.subtitle,
    selectedFilter,
    selectedFilterData,
    allIncidents,
    handleOpenCSVModal,
  ]);

  // Funciones de manejo
  const handleIncidentCreated = async (incidentData) => {
    try {
      await createIncident(incidentData);
      // Refrescar la lista de tickets después de crear uno nuevo
      refreshIncidents();
      // Mostrar popup de éxito
      setPopupMessage("Ticket creado exitosamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      // Sin saldo: el modal muestra el bloqueo con la opción de comprar
      if (isNoHoursError(error)) throw error;
      console.error("Error creating incident:", error);
      // Mostrar popup de error
      setPopupMessage(
        error.response?.data?.message || "No se pudo crear el ticket"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
      throw error;
    }
  };

  const handleRecognize = async (incidentId) => {
    try {
      setIsProcessing(incidentId);
      await openTicket(incidentId);
      // Refrescar la lista para obtener el estado actualizado
      refreshIncidents();
      // Mostrar popup de éxito
      setPopupMessage("Ticket abierto exitosamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error abriendo ticket:", error);
      setPopupMessage(
        error.response?.data?.message || "No se pudo abrir el ticket"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleComplete = async (incidentId) => {
    try {
      setIsProcessing(incidentId);
      await closeTicket(incidentId);
      // Refrescar la lista para obtener el estado actualizado
      refreshIncidents();
      // Mostrar popup de éxito
      setPopupMessage("Ticket cerrado exitosamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error cerrando ticket:", error);
      setPopupMessage(
        error.response?.data?.message || "No se pudo cerrar el ticket"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsProcessing(null);
    }
  };

  // Función para abrir el modal de gestión de tiempos
  const handleOpenTimeLogModal = async (incident) => {
    setSelectedTicketForTimeLog(incident.id);
    setIsLoadingTicketDetails(true);

    try {
      // Obtener detalles completos del ticket para tener time_log y agency_hour_balance
      const response = await apiClient.get(
        `/communication/tickets/${incident.id}/`
      );
      setTicketDetailsForTimeLog(response.data);
      setTimeLogModalOpen(true);
    } catch (error) {
      console.error("Error obteniendo detalles del ticket:", error);
      setPopupMessage("Error al cargar los detalles del ticket");
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsLoadingTicketDetails(false);
    }
  };

  // Función para cerrar el modal de gestión de tiempos
  const handleCloseTimeLogModal = () => {
    setTimeLogModalOpen(false);
    setSelectedTicketForTimeLog(null);
    setTicketDetailsForTimeLog(null);
  };

  // Función callback cuando se guarda exitosamente el tiempo
  const handleTimeLogSuccess = () => {
    // Refrescar la lista de tickets
    refreshIncidents();
    setPopupMessage("Tiempo guardado exitosamente");
    setIsPopupError(false);
    setShowSuccessPopup(true);
  };

  // Función para formatear minutos
  const formatMinutes = (minutes) => {
    if (!minutes || minutes === 0) return "0m";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    }
    return `${mins}m`;
  };

  // Funciones para manejar el menú desplegable
  const handleMenuOpen = (event, incident) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedIncidentForMenu(incident);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedIncidentForMenu(null);
  };

  const menuOpen = Boolean(menuAnchorEl);

  // Función para abrir el modal de asignar y clasificar
  const handleOpenAssignModal = (incident) => {
    setSelectedTicketForAssign(incident);
    setAssignFormData({
      assigned_to: incident.assigned_to_info?.id || null,
      category: incident.category || null,
    });
    setIsAssignModalOpen(true);
    handleMenuClose();
  };

  // Función para cerrar el modal de asignar y clasificar
  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedTicketForAssign(null);
    setAssignFormData({
      assigned_to: null,
      category: null,
    });
  };

  // Función para guardar asignación y clasificación
  const handleSaveAssignment = async () => {
    if (!selectedTicketForAssign) return;

    try {
      const payload = {};
      if (assignFormData.assigned_to !== null) {
        payload.assigned_to = assignFormData.assigned_to;
      }
      if (assignFormData.category !== null) {
        payload.category = assignFormData.category;
      }

      await apiClient.patch(
        `/communication/tickets/${selectedTicketForAssign.id}/`,
        payload
      );

      // Refrescar la lista
      refreshIncidents();
      handleCloseAssignModal();

      // Mostrar popup de éxito
      setPopupMessage("Ticket asignado y clasificado exitosamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error asignando y clasificando ticket:", error);
      setPopupMessage(
        error.response?.data?.message || "No se pudo actualizar el ticket"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    }
  };

  // Función para abrir el modal de descarga CSV
  const handleOpenCSVModal = useCallback((exportType) => {
    setCsvExportType(exportType);
    // Sincronizar el rango de fechas del modal con los filtros aplicados
    setCSVDateRange({
      startDate: appliedAdvancedFilters.createdAtAfter || "",
      endDate: appliedAdvancedFilters.createdAtBefore || "",
    });
    setCsvAgency(
      currentUser?.role === "superadmin"
        ? appliedAdvancedFilters.agency?.value || selectedFilter || null
        : null
    );
    setIsCSVModalOpen(true);
  }, [
    appliedAdvancedFilters.createdAtAfter,
    appliedAdvancedFilters.createdAtBefore,
    appliedAdvancedFilters.agency,
    currentUser?.role,
    selectedFilter,
  ]);

  // Función para cerrar el modal de descarga CSV
  const handleCloseCSVModal = () => {
    setIsCSVModalOpen(false);
    setCSVDateRange({ startDate: "", endDate: "" });
    setCsvAgency(null);
  };

  // Función para descargar la tabla completa en CSV con rango de fechas
  const handleDownloadTableCSV = useCallback(async () => {
    try {
      // Construir los filtros para obtener TODOS los tickets (sin paginación)
      const downloadFilters = {
        search: searchTerm,
        status: statusFilterValue,
      };

      // Aplicar filtros avanzados
      const advancedAgencyId = appliedAdvancedFilters.agency?.value || null;
      if (currentUser?.role === "superadmin") {
        if (csvAgency) {
          downloadFilters.agency = csvAgency;
        } else if (advancedAgencyId) {
          downloadFilters.agency = advancedAgencyId;
        } else if (selectedFilter) {
          downloadFilters.agency = selectedFilter;
        }
        if (appliedAdvancedFilters.client?.value) {
          downloadFilters.client = appliedAdvancedFilters.client.value;
        }
      } else if (currentUser?.role === "agency_admin") {
        // Agency admins export only purchase history for the selected range,
        // regardless of any active client filter in the table.
      } else if (isAgencyScopedUser) {
        if (appliedAdvancedFilters.client?.value) {
          downloadFilters.client = appliedAdvancedFilters.client.value;
        } else if (selectedFilter) {
          downloadFilters.client = selectedFilter;
        }
      }
      if (appliedAdvancedFilters.createdBy?.value) {
        downloadFilters.created_by = appliedAdvancedFilters.createdBy.value;
      }
      if (appliedAdvancedFilters.assignedTo?.value) {
        downloadFilters.assigned_to = appliedAdvancedFilters.assignedTo.value;
      }
      if (appliedAdvancedFilters.category?.value) {
        downloadFilters.category = appliedAdvancedFilters.category.value;
      }

      // Aplicar filtros de fecha del modal CSV
      if (csvDateRange.startDate) {
        downloadFilters.created_at_after = csvDateRange.startDate;
      } else if (appliedAdvancedFilters.createdAtAfter) {
        downloadFilters.created_at_after = appliedAdvancedFilters.createdAtAfter;
      }

      if (csvDateRange.endDate) {
        downloadFilters.created_at_before = csvDateRange.endDate;
      } else if (appliedAdvancedFilters.createdAtBefore) {
        downloadFilters.created_at_before = appliedAdvancedFilters.createdAtBefore;
      }

      // Construir query params
      const queryParams = new URLSearchParams();
      Object.entries(downloadFilters).forEach(([key, value]) => {
        if (value && value !== "all") {
          queryParams.append(key, value);
        }
      });

      // Usar el nuevo endpoint export_csv que devuelve todos los resultados sin paginación
      const endpoint = `/communication/tickets/export_csv/?${queryParams.toString()}`;
      const response = await apiClient.get(endpoint);

      const allTicketsData = response.data.results || [];
      const purchaseSummary = response.data.purchase_summary?.agencies || [];
      const purchaseHistory = response.data.purchase_history || [];

      if (allTicketsData.length === 0 && purchaseSummary.length === 0 && purchaseHistory.length === 0) {
        setPopupMessage("No hay datos para descargar en el rango seleccionado");
        setIsPopupError(true);
        setShowSuccessPopup(true);
        handleCloseCSVModal();
        return;
      }

      // Convertir a formato de incidentes
      const incidentsForCSV = allTicketsData.map((ticket) => ({
        id: ticket.id,
        title: ticket.title,
        description: ticket.description,
        status: ticket.status,
        agency: ticket.agency_info,
        client: ticket.client_info,
        created_by: {
          id: ticket.created_by?.id,
          full_name: ticket.created_by?.full_name,
          email: ticket.created_by?.email,
          role: ticket.created_by?.role,
        },
        thread_id: ticket.thread_id,
        created_at: ticket.created_at,
        updated_at: ticket.updated_at,
        metrics: {
          total_messages: ticket.metrics?.total_messages || 0,
          total_root_messages: ticket.metrics?.total_root_messages || 0,
          total_comments: ticket.metrics?.total_comments || 0,
          open_actions: ticket.metrics?.open_actions || 0,
          resolved_actions: ticket.metrics?.resolved_actions || 0,
          active_important_notices: ticket.metrics?.active_important_notices || 0,
          first_response_minutes: ticket.metrics?.first_response_minutes,
          last_message_at: ticket.metrics?.last_message_at,
        },
        time_log: ticket.time_log || null,
        agency_hour_balance: ticket.agency_hour_balance || null,
        assigned_to_info: ticket.assigned_to_info || null,
        category: ticket.category || null,
      }));

      const csvData = [];

      if (currentUser?.role === "agency_admin") {
        csvData.push(["Historico compras de horas"]);
        csvData.push(getPurchaseHistoryCSVHeaders());
        csvData.push(
          ...purchaseHistory.map((purchase) => purchaseHistoryToCSVRow(purchase))
        );
      } else {
        if (purchaseSummary.length > 0) {
          csvData.push(["Resumen compras de horas"]);
          csvData.push(getPurchaseSummaryCSVHeaders());
          csvData.push(...purchaseSummary.map((summary) => purchaseSummaryToCSVRow(summary)));
          csvData.push([]);
        }

        if (purchaseHistory.length > 0) {
          csvData.push(["Histórico compras de horas"]);
          csvData.push(getPurchaseHistoryCSVHeaders());
          csvData.push(...purchaseHistory.map((purchase) => purchaseHistoryToCSVRow(purchase)));
          csvData.push([]);
        }

        if (incidentsForCSV.length > 0) {
          csvData.push(["Tickets"]);
          csvData.push(getIncidentCSVHeaders());
          csvData.push(...incidentsForCSV.map((incident) => incidentToCSVRow(incident)));
        }
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const dateRangeSuffix = csvDateRange.startDate || csvDateRange.endDate
        ? `_${csvDateRange.startDate || 'inicio'}_${csvDateRange.endDate || 'fin'}`
        : '';
      const filename = currentUser?.role === "agency_admin"
        ? `historico_compras_bono_tickets_${timestamp}${dateRangeSuffix}`
        : `tickets_${timestamp}${dateRangeSuffix}`;

      downloadCSV(csvData, filename);

      // Mostrar popup de éxito
      setPopupMessage(
        currentUser?.role === "agency_admin"
          ? `CSV descargado: ${purchaseHistory.length} compras del rango seleccionado`
          : `CSV descargado: ${incidentsForCSV.length} tickets y ${purchaseHistory.length} compras de horas`
      );
      setIsPopupError(false);
      setShowSuccessPopup(true);

      // Cerrar modal
      handleCloseCSVModal();
    } catch (error) {
      console.error("Error descargando CSV:", error);
      setPopupMessage("Error al descargar los tickets");
      setIsPopupError(true);
      setShowSuccessPopup(true);
    }
  }, [
    searchTerm,
    statusFilterValue,
    appliedAdvancedFilters,
    currentUser?.role,
    selectedFilter,
    isAgencyScopedUser,
    csvDateRange,
    csvAgency,
  ]);

  const handleDownloadStaffCSV = async () => {
    const params = new URLSearchParams();
    if (csvAgency) params.append("agency", csvAgency);
    if (csvDateRange.startDate) params.append("created_at_after", csvDateRange.startDate);
    if (csvDateRange.endDate) params.append("created_at_before", csvDateRange.endDate);
    try {
      const { data } = await apiClient.get(
        `/communication/tickets/staff_summary/?${params.toString()}`
      );
      if (data.summary.length === 0) {
        setPopupMessage("No hay tickets asignados en el rango seleccionado");
        setIsPopupError(true);
      } else {
        const { startDate, endDate } = csvDateRange;
        const toEsDate = (value) => value.split("-").reverse().join("/");
        const rangeLabel =
          startDate || endDate
            ? `${startDate ? toEsDate(startDate) : "inicio"} – ${endDate ? toEsDate(endDate) : "hoy"}`
            : "Todas las fechas";
        const timestamp = new Date().toISOString().split("T")[0];
        const rangeSuffix = startDate || endDate ? `_${startDate || "inicio"}_${endDate || "fin"}` : "";
        downloadCSV(
          [
            ["Rango de fechas", rangeLabel],
            [],
            ["Resumen por persona"],
            getStaffSummaryCSVHeaders(),
            ...data.summary.map(staffSummaryToCSVRow),
            [],
            ["Detalle de tickets"],
            getStaffTicketCSVHeaders(),
            ...data.tickets.map((ticket) =>
              staffTicketToCSVRow(ticket, (statusConfig[ticket.status] || statusConfig.new).label)
            ),
          ],
          `tickets_horas_freelancers_${timestamp}${rangeSuffix}`
        );
        setPopupMessage(`CSV descargado: ${data.summary.length} personas, ${data.tickets.length} tickets`);
        setIsPopupError(false);
      }
      handleCloseCSVModal();
    } catch (error) {
      console.error("Error descargando CSV:", error);
      setPopupMessage("Error al descargar el resumen de freelancers");
      setIsPopupError(true);
    }
    setShowSuccessPopup(true);
  };

  // Configuración de filtros para el modal genérico
  const filterConfigs = React.useMemo(() => [
    {
      key: "agency",
      title: "Agencia",
      description: "Escoge la agencia sobre la que quieres filtrar.",
      icon: Building2,
      options: agencyOptions,
      loading: isLoadingFilterAgencies,
      disabled: isGlobalAgencyLocked,
      placeholder: "Selecciona una agencia",
      loadingPlaceholder: "Cargando agencias...",
      fallbackOption: globalAgencyOption,
      showForRoles: ["superadmin"],
      role: currentUser?.role,
    },
    {
      key: "client",
      title: "Cliente",
      description: "Filtra los tickets asociados a un cliente concreto.",
      icon: Users,
      options: clientOptions,
      loading: isLoadingFilterClients,
      disabled: isGlobalClientLocked,
      placeholder: "Selecciona un cliente",
      loadingPlaceholder: "Cargando clientes...",
      fallbackOption: globalClientOption,
      role: currentUser?.role,
    },
    {
      key: "createdBy",
      title: "Creado por",
      description: "Delimita los tickets por el creador.",
      icon: UserRound,
      options: creatorOptions,
      loading: isLoadingAgencyWorkers,
      disabled: false,
      placeholder: creatorPlaceholderText,
      loadingPlaceholder: "Cargando usuarios...",
      fallbackOption: null,
      role: currentUser?.role,
    },
    {
      key: "assignedTo",
      title: "Asignado a",
      description: "Filtra por tickets asignados a un usuario concreto.",
      icon: UserCheck,
      options: assignedToOptions,
      loading: isLoadingSuperadmins,
      disabled: false,
      placeholder: "Selecciona un usuario",
      loadingPlaceholder: "Cargando usuarios...",
      fallbackOption: null,
      showForRoles: ["superadmin"],
      role: currentUser?.role,
    },
    {
      key: "category",
      title: "Categoría",
      description: "Filtra por tipo de ticket.",
      icon: Tag,
      options: categoryOptions,
      loading: false,
      disabled: false,
      placeholder: "Selecciona una categoría",
      loadingPlaceholder: "",
      fallbackOption: null,
      showForRoles: ["superadmin"],
      role: currentUser?.role,
    },
    {
      type: "date-range",
      ui: "calendar-range",
      key: "createdAt",
      startKey: "createdAtAfter",
      endKey: "createdAtBefore",
      title: "Fecha de creación",
      description: "Filtra por rango de fechas de creación.",
      icon: Calendar,
      role: currentUser?.role,
    },
  ], [
    agencyOptions,
    clientOptions,
    creatorOptions,
    assignedToOptions,
    categoryOptions,
    isLoadingFilterAgencies,
    isLoadingFilterClients,
    isLoadingAgencyWorkers,
    isLoadingSuperadmins,
    isGlobalAgencyLocked,
    isGlobalClientLocked,
    globalAgencyOption,
    globalClientOption,
    creatorPlaceholderText,
    currentUser?.role,
  ]);

  const isSuperadmin = currentUser?.role === "superadmin";

  const renderTicketActions = (incident) => {
    const { canRecognize, canComplete } = canManageIncident(currentUser, incident);
    const isCurrentlyProcessing = isProcessing === incident.id;

    return (
      <>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.5, md: 1 },
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "nowrap",
          }}
        >
          {canRecognize && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRecognize(incident.id);
              }}
              onMouseEnter={() => setIsHoveringButtons(true)}
              onMouseLeave={() => setIsHoveringButtons(false)}
              disabled={isCurrentlyProcessing}
              sx={{
                minWidth: { xs: "60px", md: "70px" },
                borderColor: neutrals.gray[200],
                color: "rgba(0,0,0,1)",
                fontSize: { xs: "0.7rem", md: "0.875rem" },
                padding: { xs: "4px 8px", md: "6px 12px" },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {isCurrentlyProcessing ? (
                <CircularProgress size={12} />
              ) : (
                "Abrir"
              )}
            </Button>
          )}
          {/* Mostrar menu para tickets new y open (solo superadmin) */}
          {currentUser?.role === "superadmin" &&
            (incident.status === "new" || incident.status === "open") && (
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, incident)}
                onMouseEnter={() => setIsHoveringButtons(true)}
                onMouseLeave={() => setIsHoveringButtons(false)}
                sx={{
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                <MoreVertical size={16} />
              </IconButton>
            )}
          {/* Mostrar "Ver ticket" solo para usuarios que NO son superadmin */}
          {currentUser?.role !== "superadmin" && (
            <Box
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/tickets/${incident.id}?t=${Date.now()}`
                );
              }}
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: theme.palette.text.primary,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
                whiteSpace: "nowrap",
                transition:
                  "color 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  color: theme.palette.primary.main,
                },
                "&:active": {
                  color: theme.palette.primary.main,
                  animation: "pulseClick 0.4s ease",
                },
                "@keyframes pulseClick": {
                  "0%": {
                    color: theme.palette.text.primary,
                  },
                  "50%": {
                    color: theme.palette.primary.main,
                    transform: "scale(1.05)",
                  },
                  "100%": {
                    color: theme.palette.primary.main,
                    transform: "scale(1)",
                  },
                },
              }}
            >
              Ver ticket
              <ArrowRight
                size={14}
                style={{ marginLeft: "2px" }}
              />
            </Box>
          )}
        </Box>
        {menuOpen &&
          selectedIncidentForMenu?.id === incident.id && (
            <DropdownMenuContent
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Opción de asignar y clasificar (solo para new y open) */}
              {currentUser?.role === "superadmin" &&
                (selectedIncidentForMenu?.status === "new" ||
                  selectedIncidentForMenu?.status === "open") && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenAssignModal(
                        selectedIncidentForMenu
                      );
                    }}
                  >
                    <UserCheck size={16} />
                    Asignar y clasificar
                  </DropdownMenuItem>
                )}
              {canComplete &&
                selectedIncidentForMenu?.status === "open" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleComplete(
                        selectedIncidentForMenu.id
                      );
                      handleMenuClose();
                    }}
                    disabled={
                      isProcessing ===
                      selectedIncidentForMenu.id
                    }
                  >
                    {isProcessing ===
                      selectedIncidentForMenu.id ? (
                      <CircularProgress size={12} />
                    ) : (
                      <>
                        <X
                          size={16}
                        />
                        Cerrar ticket
                      </>
                    )}
                  </DropdownMenuItem>
                )}
              {currentUser?.role === "superadmin" &&
                selectedIncidentForMenu?.status === "open" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenTimeLogModal(
                        selectedIncidentForMenu
                      );
                      handleMenuClose();
                    }}
                    disabled={
                      isLoadingTicketDetails &&
                      selectedTicketForTimeLog ===
                      selectedIncidentForMenu?.id
                    }
                  >
                    {isLoadingTicketDetails &&
                      selectedTicketForTimeLog ===
                      selectedIncidentForMenu?.id ? (
                      <CircularProgress size={12} />
                    ) : (
                      <>
                        <Timer
                          size={16}
                        />
                        {selectedIncidentForMenu?.time_log
                          ? "Editar tiempo"
                          : "Registrar tiempo"}
                      </>
                    )}
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          )}
      </>
    );
  };

  const ticketColumns = [
    {
      key: "id",
      header: <div id="id" style={{ marginLeft: "10px", width: "fit-content" }}>ID</div>,
      tooltip: "Identificador único del ticket",
      render: (incident) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              ml: { xs: 0, md: 1 },
              fontSize: { xs: "0.75rem", md: "0.875rem" },
            }}
          >
            #{incident.id}
          </Typography>
          <UnreadThreadBadge
            count={incident.unopened_unread_notifications}
          />
          {currentUser?.role === "superadmin" && incident.is_urgent && (
            <Tooltip title="Ticket urgente" arrow>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: theme.palette.error.main,
                }}
              >
                <AlertTriangle size={14} />
              </Box>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      key: "title",
      header: "Título",
      tooltip: "Título y resumen del ticket",
      minWidth: { xs: "120px", md: "100px" },
      render: (incident) => (
        <Box sx={{ maxWidth: { xs: 160, md: 300 } }}>
          <Tooltip title={incident.title} arrow>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {incident.title}
            </Typography>
          </Tooltip>
          <Tooltip title={incident.description} arrow>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {incident.description}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: "status",
      header: "Estado",
      tooltip: "Estado actual del ticket",
      minWidth: { xs: "70px", md: "50px" },
      render: (incident) => (
        <Badge
          variant="outline"
          color={incidentStatusColorMap[incident.status]}
        >
          {(statusConfig[incident.status] || statusConfig.new).label}
        </Badge>
      ),
    },
    {
      key: "assigned_to",
      header: "Asignado a",
      tooltip: "Usuario asignado al ticket",
      minWidth: { xs: "90px", md: "100px" },
      hidden: !isSuperadmin,
      render: (incident) =>
        incident.assigned_to_info ? (
          <Tooltip title={incident.assigned_to_info.full_name} arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <UserCheck
                size={12}
                color={theme.palette.text.secondary}
              />
              <Typography
                variant="caption"
                color="text.primary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                }}
              >
                {incident.assigned_to_info.full_name}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
          >
            -
          </Typography>
        ),
    },
    {
      key: "client",
      header: isSuperadmin ? "Agencia/Cliente" : "Cliente",
      tooltip: isSuperadmin
        ? "Relación de agencia y cliente del ticket"
        : "Cliente al que pertenece el ticket",
      minWidth: { xs: "100px", md: "80px" },
      render: (incident) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          {currentUser?.role === "superadmin" && incident.agency && (
            <Tooltip title={incident.agency.name} arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 24,
                    width: 24,
                    flexShrink: 0,
                  }}
                >
                  <AgencyIcon
                    size={20}
                    color={theme.palette.text.secondary}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: { xs: "0.7rem", md: "0.75rem" },
                  }}
                >
                  {incident.agency.name}
                </Typography>
              </Box>
            </Tooltip>
          )}
          {incident.client ? (
            <Tooltip title={incident.client.name} arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Avatar
                  name={incident.client.name}
                  src={getAvatarImageUrl(incident.client.logo) || undefined}
                  size="sm"
                />
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: { xs: "0.7rem", md: "0.75rem" },
                  }}
                >
                  {incident.client.name}
                </Typography>
                {isClientReadOnly(currentUser, incident.client) && <ReadOnlyBadge />}
              </Box>
            </Tooltip>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 24,
                  width: 24,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  -
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: "created_by",
      header: "Creado por",
      tooltip: "Usuario que creó el ticket",
      minWidth: { xs: "90px", md: "80px" },
      render: (incident) => (
        <Tooltip title={incident.created_by.full_name} arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Avatar
              name={incident.created_by.full_name}
              src={getAvatarImageUrl(incident.created_by.profile_image) || undefined}
              size="sm"
            />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {incident.created_by.full_name}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "category",
      header: "Categoría",
      tooltip: "Clasificación funcional del ticket",
      minWidth: { xs: "80px", md: "90px" },
      hidden: !isSuperadmin,
      render: (incident) =>
        incident.category ? (
          <Badge
            variant="outline"
            color="blue"
           
          >
            {incident.category === "soporte"
              ? "Soporte"
              : incident.category === "mentoria"
                ? "Mentoría"
                : incident.category === "consulta"
                  ? "Consulta"
                  : incident.category === "kit_digital"
                    ? "Kit Digital"
                    : incident.category === "comercial"
                      ? "Comercial"
                    : incident.category}
          </Badge>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
          >
            -
          </Typography>
        ),
    },
    {
      key: "time",
      header: "Tiempo",
      tooltip: "Tiempo imputado o consumido por el ticket",
      minWidth: { xs: "70px", md: "90px" },
      render: (incident) =>
        incident.time_log ? (
          <Tooltip
            title={
              incident.time_log.work_description
                ? `${formatMinutes(
                  incident.time_log.minutes_consumed
                )} - ${incident.time_log.work_description}`
                : formatMinutes(
                  incident.time_log.minutes_consumed
                )
            }
            arrow
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: theme.palette.text.secondary,
              }}
            >
              <Timer size={12} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                  whiteSpace: "nowrap",
                }}
              >
                {formatMinutes(
                  incident.time_log.minutes_consumed
                )}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.7rem", md: "0.75rem" },
            }}
          >
            -
          </Typography>
        ),
    },
    {
      key: "created_at",
      header: "Fecha",
      tooltip: "Fecha de creación del ticket",
      minWidth: { xs: "90px", md: "90px" },
      render: (incident) => (
        <Tooltip
          title={new Date(
            incident.created_at
          ).toLocaleDateString("es-ES", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Clock
              size={12}
              color={theme.palette.text.secondary}
            />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {new Date(incident.created_at).toLocaleDateString(
                "es-ES",
                {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                }
              )}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "actions",
      header: "Acciones",
      tooltip: "Acciones disponibles para gestionar el ticket",
      align: "center",
      minWidth: { xs: "120px", md: "150px" },
      render: renderTicketActions,
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <DataTable
        columns={ticketColumns}
        rows={visibleIncidents}
        loading={isLoadingIncidents}
        emptyIcon={Bell}
        emptyMessage={
          searchTerm
            ? "No se encontraron tickets con los filtros"
            : "No hay tickets disponibles"
        }
        onRowClick={(incident) => {
          navigate(`/tickets/${incident.id}?t=${Date.now()}`);
        }}
        getRowSx={() => ({
          transition: "background-color 0.2s ease",
          "&:hover, &.MuiTableRow-hover:hover": {
            backgroundColor: isHoveringButtons ? "transparent" : neutrals.gray[50],
            "& .hover-only-button": {
              opacity: 1,
            },
          },
        })}
        tableMinWidth={350}
        maxHeight="calc(100vh - 290px)"
        minHeight="calc(100vh - 290px)"
        // Toolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Buscar tickets..."
        tabs={[
          {
            value: "all",
            label: "Todos",
            count: statusStats.new + statusStats.open + statusStats.closed,
          },
          { value: "new", label: "Nuevos", count: statusStats.new },
          { value: "open", label: "Abiertos", count: statusStats.open },
          { value: "closed", label: "Cerrados", count: statusStats.closed },
        ]}
        activeTab={selectedStatus}
        onTabChange={handleStatusChange}
        filterChips={displayedFilterChips.map((chip) => ({
          ...chip,
          key: `${chip.key}-${chip.label}`,
        }))}
        onFilterClick={handleOpenFilterModal}
        activeFiltersCount={advancedFilterChips.length}
        filterTooltip="Editar filtros"
        // Paginación
        page={currentPage}
        rowsPerPage={pageSize}
        totalCount={totalCount}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <IncidentFiltersModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        title="Filtros avanzados"
        filterConfigs={filterConfigs}
        draftFilters={draftAdvancedFilters}
        onChange={handleDraftFilterChange}
        onApply={handleApplyAdvancedFilters}
        onClearAll={handleClearDraftFilters}
      />

      {/* Modales */}
      <CreateIncidentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        currentUser={currentUser}
        onSubmit={handleIncidentCreated}
        clients={backendClients || []}
      />

      {/* Modal de gestión de tiempos */}
      {selectedTicketForTimeLog && (
        <TimeLogModal
          isOpen={timeLogModalOpen}
          onClose={handleCloseTimeLogModal}
          ticketId={selectedTicketForTimeLog}
          ticketData={ticketDetailsForTimeLog}
          onSuccess={handleTimeLogSuccess}
        />
      )}

      {/* Modal de asignar y clasificar */}
      <UnifiedModal
        open={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        title="Asignar y clasificar ticket"
        maxWidth="sm"
      >
        <Stack spacing={3}>
          <Box>
            <Label sx={{ mb: 1 }}>Asignar a</Label>
            <CustomSelector
              value={assignFormData.assigned_to}
              onChange={(value) =>
                setAssignFormData((prev) => ({ ...prev, assigned_to: value }))
              }
              options={assignedToOptions}
              placeholder="Selecciona un usuario"
              searchable
            />
          </Box>

          <Box>
            <Label sx={{ mb: 1 }}>Categoría</Label>
            <CustomSelector
              value={assignFormData.category}
              onChange={(value) =>
                setAssignFormData((prev) => ({ ...prev, category: value }))
              }
              options={categoryOptions}
              placeholder="Selecciona una categoría"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
            <Button variant="outline" onClick={handleCloseAssignModal}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAssignment}>
              Guardar
            </Button>
          </Box>
        </Stack>
      </UnifiedModal>

      {/* Modal de descarga CSV con rango de fechas */}
      <UnifiedModal
        open={isCSVModalOpen}
        onClose={handleCloseCSVModal}
        title={
          currentUser?.role !== "superadmin"
            ? "Descargar tickets en CSV"
            : csvExportType === "staff"
              ? "Descargar tickets y horas por freelancer"
              : "Descargar tickets y bonos en CSV"
        }
        maxWidth="sm"
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "70vh" }}>
          {/* Contenido scrollable */}
          <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                {csvExportType === "staff"
                  ? "Resumen de tickets asignados, cerrados y horas registradas por cada freelancer, y debajo el detalle de cada ticket con su fecha. Se cuentan los tickets creados en el rango de fechas elegido (ambos días incluidos). Si no seleccionas agencia, se incluyen todas."
                  : currentUser?.role === "agency_admin"
                  ? "Selecciona el rango de fechas del que quieres descargar el historico de compras del bono de horas extra de tu agencia. El filtro de cliente no se aplicará en esta descarga."
                  : (
                    <>
                      Selecciona una agencia y/o un rango de fechas para descargar los tickets que coincidan con los filtros aplicados. <b>El CSV también incluirá el resumen e histórico de bono de horas comprados de la agencia seleccionada.</b> Si no seleccionas agencia, se incluirán todas las agencias.
                    </>
                  )}
              </Typography>

              {currentUser?.role === "superadmin" && (
                <Box>
                  <Label sx={{ mb: 1 }}>Agencia</Label>
                  <CustomSelector
                    value={csvAgency ?? "__any__"}
                    onChange={(value) =>
                      setCsvAgency(value === "__any__" ? null : value)
                    }
                    options={csvAgencyOptions}
                    placeholder={
                      isLoadingFilterAgencies
                        ? "Cargando agencias..."
                        : "Mostrar todas"
                    }
                    disabled={isLoadingFilterAgencies}
                    searchable
                    placeholderValues={["__any__"]}
                  />
                </Box>
              )}

              <Box>
                <Label sx={{ mb: 1 }}>Rango de fechas</Label>
                <DateRangeFilterField
                  startValue={csvDateRange.startDate}
                  endValue={csvDateRange.endDate}
                  onStartChange={(value) =>
                    setCSVDateRange((prev) => ({ ...prev, startDate: value }))
                  }
                  onEndChange={(value) =>
                    setCSVDateRange((prev) => ({ ...prev, endDate: value }))
                  }
                />
              </Box>
            </Stack>
          </Box>

          {/* Botones fijos en la parte inferior */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              pt: 3,
              mt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              position: "sticky",
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button variant="outline" onClick={handleCloseCSVModal}>
              Cancelar
            </Button>
            <Button onClick={csvExportType === "staff" ? handleDownloadStaffCSV : handleDownloadTableCSV}>
              <Download size={16} style={{ marginRight: "8px" }} />
              Descargar CSV
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Success/Error Popup */}
      <SuccessPopup
        message={popupMessage}
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        autoHideDuration={3000}
        error={isPopupError}
      />
    </Box>
  );
}
